// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

type ServiceStatusModalProps = {
  open: boolean;
  onClose: () => void;
  missing: Array<{ name: string; url: string | null; host: string | null; statusCode: number | null }>;
  checkedAt: string | null;
  hasWarnings: boolean;
  warnings: Array<{ timestamp: string; service: string; body: string }>;
};

export const ServiceStatusModal = ({
  open,
  onClose,
  missing,
  checkedAt,
  hasWarnings,
  warnings,
}: ServiceStatusModalProps) => {
  const { t } = useI18n();
  const missingSummary = missing.map((entry) => entry.name).join(', ');
  const continueText =
    missing.length > 0
      ? t('serviceStatus.continueMissing', { missing: missingSummary })
      : t('serviceStatus.close');

  return (
    <Modal transparent visible={open} animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <View style={styles.card}>
          <Text style={styles.title}>{t('serviceStatus.title')}</Text>
          {missing.length > 0 ? (
            <View style={styles.list}>
                {missing.map((entry) => (
                  <View key={`${entry.name}-${entry.url ?? 'no-url'}`} style={styles.itemRow}>
                    <Text style={styles.item}>{entry.name}</Text>
                    {entry.url ? <Text style={styles.itemUrl}>{entry.url}</Text> : null}
                    <View style={styles.metaRow}>
                      <Text style={styles.itemMeta}>
                        {entry.host ? t('serviceStatus.host', { host: entry.host }) : t('serviceStatus.unknownHost')}
                      </Text>
                      {entry.statusCode != null ? (
                        <Text style={styles.itemMeta}>{t('serviceStatus.httpCode', { code: entry.statusCode })}</Text>
                      ) : null}
                      <Text style={styles.itemMeta}>
                        {entry.envVar ? t('serviceStatus.envVar', { envVar: entry.envVar }) : t('serviceStatus.unknownEnvVar')}
                      </Text>
                      {entry.resolvedIp ? (
                        <Text style={styles.itemMeta}>{t('serviceStatus.ipAddress', { ip: entry.resolvedIp })}</Text>
                      ) : null}
                    </View>
                  </View>
                ))}
              </View>
          ) : (
            <>
              <Text style={styles.okText}>
                {hasWarnings ? t('serviceStatus.warningsFound') : t('serviceStatus.allOk')}
              </Text>
              {hasWarnings && warnings.length > 0 ? (
                <View style={styles.warnings}>
                  {warnings.map((entry, index) => (
                    <View key={`${entry.timestamp}-${index}`} style={styles.warningRow}>
                      <Text style={styles.warningMeta}>
                        {entry.timestamp || t('serviceStatus.unknownTimestamp')}
                      </Text>
                      <Text style={styles.warningMeta}>
                        {entry.service || t('serviceStatus.unknownService')}
                      </Text>
                      <Text style={styles.warningBody} numberOfLines={3}>
                        {entry.body || t('serviceStatus.unknownBody')}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </>
          )}
          {checkedAt ? (
            <Text style={styles.meta}>{t('serviceStatus.checkedAt', { time: checkedAt })}</Text>
          ) : null}
          <Pressable style={styles.close} onPress={onClose}>
            <Text style={styles.closeText}>{continueText}</Text>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.14),
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    minWidth: 260,
    padding: 18,
    borderRadius: 12,
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.35,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  title: {
    color: appColors.surface.light,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  list: {
    marginBottom: 12,
    gap: 6,
  },
  item: {
    color: appColors.status.dangerTextSoft,
    fontSize: 14,
  },
  itemRow: {
    gap: 2,
  },
  itemUrl: {
    color: appColors.status.dangerText,
    fontSize: 12,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  itemMeta: {
    color: appColors.accent.soft,
    fontSize: 12,
    marginRight: 10,
  },
  okText: {
    color: appColors.text.positive,
    fontSize: 14,
    marginBottom: 12,
  },
  warnings: {
    marginBottom: 12,
    gap: 10,
  },
  warningRow: {
    gap: 4,
  },
  warningMeta: {
    color: appColors.text.warm,
    fontSize: 12,
  },
  warningBody: {
    color: appColors.surface.light,
    fontSize: 12,
  },
  meta: {
    color: appColors.text.muted,
    fontSize: 12,
    marginBottom: 12,
  },
  close: {
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: appColors.text.secondary,
  },
  closeText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
});
